import React, { useEffect, useState } from 'react'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Container, Grid, CircularProgress, Chip } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import WarningRoundedIcon from '@material-ui/icons/WarningRounded'

//HELPERS
import { embedYoutubeVideo } from '../../utils/helpers'

//COMPONENTS
import MenuCategory from '../../components/MenuCategory/MenuCategory'
import BookerForm from '../../components/BookerForm/BookerForm'
import OrderDialog from '../../components/OrderDialog/OrderDialog'
import Carousel from 'react-material-ui-carousel'

//API
import { getProducts } from '../../services/FirebaseServices/menuServices'
import {
  getExperienceById,
  getExperienceGallery,
  getScheduleExperienceCollection,
} from '../../services/FirebaseServices/experienceServices'
import { getAgencyById } from '../../services/FirebaseServices/agencyServices'

//REDUX
import {
  makeSelectRestaurantOpen,
  set_restaurant_open,
  clean_restaurant_menu,
} from '../../redux/ducks/restaurantMenu'

import { clean_verify_reservation } from '../../redux/ducks/verifyReservation'

import { clean_reservation_info } from '../../redux/ducks/reservationInfo'

const stateSelector = createSelector(
  [makeSelectRestaurantOpen],
  (restaurantOpen) => ({ restaurantOpen }),
)

const actionDispatch = (dispatch) => ({
  setRestaurantOpen: (restaurantOpen) =>
    dispatch(set_restaurant_open(restaurantOpen)),
  cleanRestaurantMenu: () => dispatch(clean_restaurant_menu()),
  cleanVerifyReservation: () => dispatch(clean_verify_reservation()),
  cleanReservationInfo: () => dispatch(clean_reservation_info()),
})

const RestaurantMenu = ({ agency, singleExperience }) => {
  const [categoryProducts, setCategoryProducts] = useState([])
  const [menuId, setMenuId] = useState('')
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [gallery, setGallery] = useState([])

  const { restaurantOpen } = useSelector(stateSelector)
  const {
    setRestaurantOpen,
    cleanRestaurantMenu,
    cleanVerifyReservation,
    cleanReservationInfo,
  } = actionDispatch(useDispatch())

  useEffect(() => {
    const unsubscribeProducts = fetchProducts()
    const unsubcribeSchedule = fetchSchedule()
    fetchData()
    return function cleanup() {
      unsubscribeProducts()
      unsubcribeSchedule()
      cleanRestaurantMenu()
      cleanVerifyReservation()
      cleanReservationInfo()
    }
  }, [])

  function fetchProducts() {
    return getProducts(
      agency.key,
      singleExperience.key,
      singleExperience.menuId,
    ).onSnapshot((querySnapshot) => {
      let productData = []
      querySnapshot.forEach((doc) => {
        productData.push(doc.data())
      })
      //Productos que esten activos y sin borrar
      const filterProducts = productData.filter(
        (product) => product.isActive === true && product.isDelete === false,
      )

      const categories = productData.map((product) => product.category)
      //Quitar categorias repetidas
      const removeRepeatedCategories = categories.filter(
        (category, index, self) =>
          index === self.findIndex((t) => t.key === category.key),
      )

      setMenuId(singleExperience.menuId)
      setProducts(filterProducts)
      setCategoryProducts(removeRepeatedCategories)
      setIsLoading(false)
    })
  }

  async function fetchSchedule() {
    return getScheduleExperienceCollection(
      agency.key,
      singleExperience.key,
    ).onSnapshot((querySnapshot) => {
      let data = {}
      querySnapshot.forEach((doc) => (data = { ...doc.data() }))

      const { days } = data

      const currentDay = new Date().getDay()

      const schedule = days.find((item) => item.day === currentDay)

      const { openHour, closeHour } = schedule

      if (closeHour && openHour) {
        //Obtener si la hora es AM o PM
        const openHourPeriod = openHour.slice(-2)
        const closeHourPeriod = closeHour.slice(-2)

        const formatOpenHour =
          openHourPeriod === 'AM'
            ? parseInt(openHour.split(':')[0])
            : parseInt(openHour.split(':')[0]) + 12

        const formatOpenMinutes = parseInt(openHour.split(':')[1])

        const formatCloseHour =
          closeHourPeriod === 'AM'
            ? parseInt(closeHour.split(':')[0])
            : parseInt(closeHour.split(':')[0]) + 12

        const formatCloseMinutes = parseInt(closeHour.split(':')[1])

        const currentDate = new Date()
        const currentHour = currentDate.getHours()
        const currentMinutes = currentDate.getMinutes()

        if (
          (currentHour >= formatOpenHour ||
            (currentHour === formatOpenHour &&
              currentMinutes >= formatOpenMinutes)) &&
          (currentHour < formatCloseHour ||
            (currentHour === formatCloseHour &&
              currentMinutes < formatCloseMinutes))
        ) {
          setRestaurantOpen(true)
        } else {
          setRestaurantOpen(false)
        }
      } else {
        setRestaurantOpen(false)
      }
    })
  }

  async function fetchData() {
    const experienceResponse = await getExperienceById(singleExperience.key)
    const agencyResponse = await getAgencyById(agency.key)
    const galleryResponse = await getExperienceGallery(
      agencyResponse.key,
      experienceResponse.key,
    )

    const validImages = galleryResponse.filter((item) => item !== '')

    setGallery(validImages)
    setIsLoading(false)
  }

  const filterProducts = (categoryId) => {
    return products.filter((product) => product.category.key === categoryId)
  }
  const handleCarousel = () => {
    const carouselArray = []

    carouselArray.push({
      type: 'video',
      title: singleExperience.name,
      link: singleExperience.linkVideo,
    })

    gallery.forEach((item) => carouselArray.push({ type: 'photo', link: item }))

    return carouselArray
  }

  const handleCategoryWithProducts = () => {
    return categoryProducts.filter((category) => {
      const productExist = filterProducts(category.key).filter(
        (item) => item.isActive === true && item.isDelete === false,
      )

      return productExist.length
    })
  }

  return (
    <>
      <Box>
        <Container style={{ padding: 0 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {!restaurantOpen && (
                  <Box display="flex" justifyContent="center" width="100%">
                    <Alert severity="error">
                      <AlertTitle style={{ fontWeight: 'bold' }}>
                        En estos momentos estamos cerrados.
                      </AlertTitle>
                      Sin embargo, puedes ver nuestros productos y promociones;
                      pero no podras realizar pedidos hasta que abramos
                      nuevamente.
                    </Alert>
                  </Box>
                )}

                <Grid item sm={7}>
                  {!handleCategoryWithProducts().length && (
                    <Box textAlign="center">
                      <WarningRoundedIcon
                        style={{ fontSize: 120, color: '#d5d5d5' }}
                      />
                      <h1 style={{ color: '#d5d5d5' }}>
                        El restaurant no posee productos registrados.
                      </h1>
                    </Box>
                  )}
                  {handleCategoryWithProducts().map((category) => {
                    return (
                      <MenuCategory
                        category={category}
                        key={category.key}
                        products={filterProducts(category.key)}
                      />
                    )
                  })}
                </Grid>
                <Grid item sm={5}>
                  <Box mb={4}>
                    <BookerForm type="RESTAURANT" />
                  </Box>

                  {singleExperience.linkVideo ? (
                    <Carousel>
                      {handleCarousel().map((item, index) => {
                        if (item.type === 'video') {
                          return (
                            <iframe
                              title={`exp-${item.title}`}
                              width="100%"
                              height="300px"
                              src={`//www.youtube.com/embed/${embedYoutubeVideo(
                                item.link,
                              )}`}
                              key={index}
                            />
                          )
                        } else {
                          return (
                            <div
                              style={{
                                backgroundImage: `url(${item.link})`,
                                width: '100%',
                                height: '300px',
                                backgroundPosition: 'center center',
                                backgroundSize: 'cover',
                                borderRadius: '15px',
                              }}
                              key={index}
                            ></div>
                          )
                        }
                      })}
                    </Carousel>
                  ) : (
                    <Carousel>
                      {gallery.map((item, index) => {
                        return (
                          <div
                            style={{
                              backgroundImage: `url(${item})`,
                              width: '100%',
                              height: '300px',
                              backgroundPosition: 'center center',
                              backgroundSize: 'cover',
                              borderRadius: '15px',
                            }}
                            key={index}
                          ></div>
                        )
                      })}
                    </Carousel>
                  )}
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                  >
                    <p>
                      <b>Descripcion: </b>
                      {singleExperience.description}
                    </p>
                    <p>
                      <b>Ubicacion: </b>{' '}
                      {singleExperience.locator.formatted_address
                        ? singleExperience.locator.formatted_address
                        : singleExperience.locator}
                    </p>
                    <Box>
                      <h3>Servicios</h3>
                      {singleExperience.included.map((servicio) => {
                        return (
                          <Chip
                            className="chip"
                            color="primary"
                            size="small"
                            label={servicio}
                            key={servicio}
                          />
                        )
                      })}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <OrderDialog
                experienceId={singleExperience.key}
                agencyId={agency.key}
                menuId={menuId}
              />
            </>
          )}
        </Container>
      </Box>
    </>
  )
}

export default RestaurantMenu
